'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { apiClient } from '@/lib/api-client'

// Notification types
type NotificationType = 'info' | 'success' | 'warning' | 'error'

interface Notification {
  id: number
  type: NotificationType
  title: string
  message: string | null
  link_type: string | null // "pr", "po", "cr"
  link_id: string | null   // PR number, PO number, etc.
  is_read: boolean
  created_at: string | null
}

interface NotificationsResponse {
  unread_count: number
  notifications: Notification[]
}

const typeConfig: Record<
  NotificationType,
  { icon: React.ElementType; color: string; bg: string }
> = {
  info: { icon: Icons.info, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  success: {
    icon: Icons.checkCircle,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
  },
  warning: {
    icon: Icons.alertTriangle,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  error: {
    icon: Icons.alertCircle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
  },
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Just now'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

/**
 * Get the navigation URL for a notification's linked entity.
 */
function getNotificationLink(notification: Notification): string | null {
  if (!notification.link_type || !notification.link_id) return null
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  switch (notification.link_type) {
    case 'pr':
      return `${basePath}/procurement/requests/${notification.link_id}`
    case 'po':
      return `${basePath}/procurement/orders/${notification.link_id}`
    default:
      return null
  }
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onNavigate: (url: string) => void
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
}: NotificationItemProps) {
  const config = typeConfig[notification.type] || typeConfig.info
  const Icon = config.icon
  const link = getNotificationLink(notification)

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id)
    }
    if (link) {
      onNavigate(link)
    }
  }

  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl p-3',
        'transition-all duration-200 ease-out',
        'hover:scale-[1.02] active:scale-[0.98]',
        link ? 'cursor-pointer' : 'cursor-default',
        notification.is_read
          ? 'opacity-60 hover:opacity-100'
          : 'hover:bg-brand-cornflower/5 hover:shadow-sm'
      )}
      onClick={handleClick}
    >
      <div
        className={cn(
          'flex-shrink-0 rounded-lg p-2 transition-transform duration-200',
          config.bg,
        )}
      >
        <Icon className={cn('h-4 w-4', config.color)} strokeWidth={1.5} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='truncate text-sm font-medium text-foreground'>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className='mt-1.5 h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand-cornflower' />
          )}
        </div>
        {notification.message && (
          <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>
            {notification.message}
          </p>
        )}
        <div className='flex items-center gap-2 mt-1'>
          <p className='text-[10px] text-muted-foreground/60'>
            {formatRelativeTime(notification.created_at)}
          </p>
          {link && (
            <span className='text-[10px] text-brand-cornflower font-medium'>
              View →
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Polling interval in milliseconds
const POLL_INTERVAL = 30000 // 30 seconds

export function NotificationCenter() {
  const router = useRouter()
  const [notifications, setNotifications] = React.useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [_isLoading, _setIsLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)

  // Fetch notifications from the backend
  const fetchNotifications = React.useCallback(async () => {
    try {
      const data = await apiClient.get<NotificationsResponse>(
        '/api/procurement/notifications?limit=20'
      )
      setNotifications(data.notifications || [])
      setUnreadCount(data.unread_count || 0)
    } catch {
      // Silently fail — user may not be logged in or endpoint may not exist yet
    }
  }, [])

  // Initial fetch + polling
  React.useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Re-fetch when popover opens
  React.useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen, fetchNotifications])

  const markAsRead = async (id: number) => {
    try {
      await apiClient.patch(`/api/procurement/notifications/${id}/read`)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch {
      // Silently fail
    }
  }

  const markAllAsRead = async () => {
    try {
      await apiClient.patch('/api/procurement/notifications/mark-all-read')
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch {
      // Silently fail
    }
  }

  const handleNavigate = (url: string) => {
    setIsOpen(false)
    router.push(url)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='relative rounded-full text-muted-foreground hover:text-foreground'
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5',
                'flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-destructive px-1',
                'text-[10px] font-semibold text-white',
                'animate-badge-bounce'
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={markAllAsRead}
              className='h-auto p-0 text-xs text-brand-cornflower'
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className='max-h-[400px] overflow-y-auto p-2'>
          {notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle className='h-6 w-6 text-muted-foreground' />
              </div>
              <p className='text-sm font-medium text-foreground'>
                All caught up!
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                No new notifications
              </p>
            </div>
          ) : (
            <div className='space-y-1'>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className='border-t border-border/50 p-2'>
            <Button
              variant='ghost'
              className='w-full text-brand-navy'
              onClick={() => handleNavigate('/procurement/approvals')}
            >
              View all approvals
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
